import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import bcrypt from "bcryptjs";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  Card,
  CardContent,
  Divider,
  Stack,
  Container,
  Paper
} from "@mui/material";
import { 
  Save as SaveIcon, 
  Delete as DeleteIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  Business as BusinessIcon,
  Image as ImageIcon
} from "@mui/icons-material";
import ImageUpload from "../components/ImageUpload";
import ClubLogo from "../components/ClubLogo";

import MailConfigTab from '../components/MailConfigTab';
import { Tabs, Tab } from '@mui/material';
import logger from "../utils/logger";

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState({
    _id: "",
    id:"",
    name: "",
    email: "",
    role: "",
    club: { 
      _id: "",
      name: "", 
      address: { street: "", postalCode: "", city: "" },
      hasLogo: false
    },
  });

  const [passwords, setPasswords] = useState({ oldPassword: "", newPassword: "", confirmNewPassword: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [logoUpdated, setLogoUpdated] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get("/api/users/me");
      logger.info("User data fetched successfully:", response.data);
      logger.info("User id fetched successfully:", response.data.id);
      setUser(response.data);
    } catch (error) {
      setError("Fehler beim Laden des Profils");
    }
  };

  const handleEmailChange = () => {
    axios
      .put("/api/users/email", { email: user.email })
      .then(() => {
        setMessage("E-Mail wurde erfolgreich geändert!");
        setError("");
      })
      .catch(() => {
        setError("Fehler beim Ändern der E-Mail.");
        setMessage("");
      });
  };

  const handleAddressChange = () => {
    axios
      .put("/api/users/address", { address: user.club.address })
      .then(() => {
        setMessage("Adresse wurde erfolgreich geändert!");
        setError("");
      })
      .catch(() => {
        setError("Fehler beim Ändern der Adresse.");
        setMessage("");
      });
  };

  const handlePasswordChange = async () => {
    if (passwords.newPassword !== passwords.confirmNewPassword) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      setMessage("");
      return;
    }

    try {
      await axios.put("/api/users/password", {
        oldPassword: passwords.oldPassword,
        newPassword: passwords.newPassword,
      });
      setMessage("Passwort wurde erfolgreich geändert!");
      setError("");
      setPasswords({ oldPassword: "", newPassword: "", confirmNewPassword: "" });
    } catch {
      setError("Fehler beim Ändern des Passworts.");
      setMessage("");
    }
  };

  const handleLogoSuccess = () => {
    setMessage("Vereinslogo wurde erfolgreich hochgeladen!");
    setError("");
    setLogoUpdated(true);
    // Update user data to reflect logo change
    setUser(prev => ({
      ...prev,
      club: {
        ...prev.club,
        hasLogo: true
      }
    }));
  };

  const handleLogoDelete = async () => {
    try {
      await axios.delete(`/api/images/Club/${user.club._id}`);
      setMessage("Vereinslogo wurde erfolgreich gelöscht!");
      setError("");
      setLogoUpdated(true);
      // Update user data to reflect logo change
      setUser(prev => ({
        ...prev,
        club: {
          ...prev.club,
          hasLogo: false
        }
      }));
    } catch (error) {
      setError("Fehler beim Löschen des Logos");
      setMessage("");
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={2} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
          Vereinsprofil verwalten
        </Typography>

        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {user.role === 'organizer' && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              aria-label="profile tabs"
            >
              <Tab label="Profil" />
              <Tab label="Mail-Konfiguration" />
            </Tabs>
          </Box>
        )}

        {(activeTab === 0) && (
          <Stack spacing={4}>
            {/* Persönliche Informationen */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="medium">
                  Persönliche Informationen
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Stack spacing={2}>
                <TextField
                  label="Name"
                  value={user.name}
                  fullWidth
                  disabled
                  variant="outlined"
                />
                <TextField
                  label="E-Mail-Adresse"
                  type="email"
                  value={user.email}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  fullWidth
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleEmailChange}
                  startIcon={<SaveIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  E-Mail speichern
                </Button>
              </Stack>
            </Box>

            {/* Passwort ändern */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VpnKeyIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="medium">
                  Passwort ändern
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Stack spacing={2}>
                <TextField
                  label="Altes Passwort"
                  type="password"
                  value={passwords.oldPassword}
                  onChange={(e) => setPasswords({ ...passwords, oldPassword: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Neues Passwort"
                  type="password"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Neues Passwort bestätigen"
                  type="password"
                  value={passwords.confirmNewPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmNewPassword: e.target.value })}
                  fullWidth
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handlePasswordChange}
                  startIcon={<SaveIcon />}
                  disabled={!passwords.oldPassword || !passwords.newPassword || !passwords.confirmNewPassword}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Passwort ändern
                </Button>
              </Stack>
            </Box>

            {/* Vereinslogo */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ImageIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="medium">
                  Vereinslogo
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {user.club.hasLogo ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                      <ClubLogo 
                        clubId={user.club._id} 
                        hasLogo={user.club.hasLogo} 
                        size="large" 
                      />
                    </Box>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleLogoDelete}
                    >
                      Logo löschen
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" gutterBottom>
                      Kein Logo vorhanden. Lade bitte ein Logo hoch (JPG oder PNG, max. 5MB, max. 1024x1024 Pixel).
                    </Typography>
                    <ImageUpload
                      entityId={user.club._id}
                      entityType="Club"
                      onSuccess={handleLogoSuccess}
                      label="Vereinslogo"
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Vereinsinformationen */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon color="primary" />
                <Typography variant="h6" color="primary" fontWeight="medium">
                  Vereinsinformationen
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Stack spacing={2}>
                <TextField
                  label="Vereinsname"
                  value={user.club.name}
                  fullWidth
                  disabled
                  variant="outlined"
                />
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Vereinsadresse
                </Typography>
                <TextField
                  label="Straße"
                  value={user.club.address.street}
                  onChange={(e) =>
                    setUser({
                      ...user,
                      club: { ...user.club, address: { ...user.club.address, street: e.target.value } },
                    })
                  }
                  fullWidth
                />
                <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                  <TextField
                    label="PLZ"
                    value={user.club.address.postalCode}
                    onChange={(e) =>
                      setUser({
                        ...user,
                        club: { ...user.club, address: { ...user.club.address, postalCode: e.target.value } },
                      })
                    }
                    sx={{ width: '30%' }}
                  />
                  <TextField
                    label="Stadt"
                    value={user.club.address.city}
                    onChange={(e) =>
                      setUser({
                        ...user,
                        club: { ...user.club, address: { ...user.club.address, city: e.target.value } },
                      })
                    }
                    sx={{ width: '70%' }}
                  />
                </Box>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleAddressChange}
                  startIcon={<SaveIcon />}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Adresse speichern
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}

        {/* Tab 1: Mail-Config */}
        {user.role === 'organizer' && activeTab === 1 && (
          <MailConfigTab 
            organizerId={user.id}
            onMessage={setMessage}
            onError={setError}
          />
        )}

      </Paper>
    </Container>
  );
};

export default ProfilePage;